/**
 * Easy Service Kits – storefront engine.
 *
 * Responsibilities
 *  - Look a UK registration up through the AutoFinder UK app proxy (/apps/vrm-lookup).
 *  - Keep the found vehicle in localStorage under the same key the app uses ('savedVehicleInfo').
 *  - Work out the engine-size band (Small / Medium / Large) and fuel type.
 *  - Drive the custom elements used by the esk-* sections and blocks:
 *      <esk-reg-form>     registration plate input + lookup
 *      <esk-finder>       vehicle bar, manual engine-size picker and the kit cards
 *      <esk-model-card>   model-specific kit card with kit dropdown + reg + add to basket
 *      <esk-product-reg>  product page: capture reg, select the right variant, attach line item properties
 *      <esk-cart-reg>     cart page: add a missing registration to a line item
 *      <esk-addon>        one-click add-on product button
 *  - Add items to the basket and hand off to Horizon's cart drawer.
 */
import { CartAddEvent } from '@theme/events';

const STORAGE_KEY = 'savedVehicleInfo';
const LOOKUP_URL = '/apps/vrm-lookup';
const EVENT_NAME = 'esk:vehicle';
const PROP_REG = 'Registration Plate';
const PROP_VEHICLE = 'Vehicle';

const BANDS = {
  small: { key: 'small', short: 'Small', label: 'Small (Up to 1.6L)' },
  medium: { key: 'medium', short: 'Medium', label: 'Medium (1.7L - 2.5L)' },
  large: { key: 'large', short: 'Large', label: 'Large (2.6L+)' },
};

const BRAND_FIX = {
  bmw: 'BMW',
  mg: 'MG',
  ds: 'DS',
  seat: 'SEAT',
  mini: 'MINI',
  'mercedes-benz': 'Mercedes-Benz',
  vw: 'Volkswagen',
};

/* ---------------------------------------------------------------- helpers */

function normalizeReg(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);
}

function formatReg(value) {
  const reg = normalizeReg(value);
  if (/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(reg)) return `${reg.slice(0, 4)} ${reg.slice(4)}`;
  return reg;
}

function titleCase(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const fixed = BRAND_FIX[raw.toLowerCase()];
  if (fixed) return fixed;
  return raw
    .toLowerCase()
    .split(' ')
    .map((word) =>
      word
        .split('-')
        .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
        .join('-')
    )
    .join(' ');
}

function bandFor(enginecc, litres) {
  const cc = Number(enginecc);
  if (Number.isFinite(cc) && cc > 0) {
    if (cc <= 1650) return 'small';
    if (cc <= 2550) return 'medium';
    return 'large';
  }
  const l = Number.parseFloat(litres);
  if (Number.isFinite(l) && l > 0) {
    if (l <= 1.6) return 'small';
    if (l <= 2.5) return 'medium';
    return 'large';
  }
  return null;
}

function fuelKind(fuel) {
  const f = String(fuel || '').toLowerCase();
  if (!f) return null;
  if (f.includes('diesel')) return 'diesel';
  if (f.includes('petrol') || f.includes('gasoline') || f.includes('hybrid')) return 'petrol';
  if (f.includes('electric')) return 'electric';
  return 'other';
}

function bandFromTitle(title) {
  const t = String(title || '').toLowerCase();
  if (t.includes('small')) return 'small';
  if (t.includes('medium')) return 'medium';
  if (t.includes('large')) return 'large';
  return null;
}

function describe(vehicle) {
  if (!vehicle) return '';
  if (vehicle.manual) {
    const band = BANDS[vehicle.band];
    const fuel = vehicle.fuel ? titleCase(vehicle.fuel) : '';
    return [band ? band.label : '', fuel].filter(Boolean).join(' · ') || 'Engine size chosen manually';
  }
  const make = titleCase(vehicle.make);
  const model = vehicle.showFullModelName && vehicle.fullModel ? vehicle.fullModel : vehicle.model;
  const litres = vehicle.litres ? `${vehicle.litres}L` : vehicle.enginecc ? `${vehicle.enginecc}cc` : '';
  const fuel = vehicle.fuel ? titleCase(vehicle.fuel) : '';
  const year = vehicle.year ? `(${vehicle.year})` : '';
  return [make, model, litres, fuel, year].filter(Boolean).join(' ');
}

function enrich(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') return null;
  const band = vehicle.band || bandFor(vehicle.enginecc, vehicle.litres);
  return {
    ...vehicle,
    vrm: normalizeReg(vehicle.vrm),
    band,
    bandLabel: band ? BANDS[band].label : '',
    fuelKind: fuelKind(vehicle.fuel),
    label: describe(vehicle),
  };
}

function emit() {
  document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { vehicle: ESK.getVehicle() } }));
}

function money(pence) {
  const n = Number(pence) / 100;
  return `£${n.toFixed(2)}`;
}

let toastTimer = null;
function toast(message) {
  let el = document.querySelector('.esk-toast');
  if (!el) {
    el = document.createElement('div');
    el.className = 'esk-toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
}

/* ------------------------------------------------------------------- ESK */

const ESK = {
  BANDS,
  normalizeReg,
  formatReg,
  describe,
  bandFor,
  fuelKind,
  money,
  toast,

  getVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return enrich(JSON.parse(raw));
    } catch (error) {
      return null;
    }
  },

  saveVehicle(vehicle) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
    } catch (error) {
      /* storage unavailable – keep going without persistence */
    }
    emit();
  },

  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* ignore */
    }
    emit();
  },

  setManual({ band, fuel, reg }) {
    this.saveVehicle({
      manual: true,
      vrm: normalizeReg(reg),
      band,
      fuel: fuel || '',
      make: '',
      model: '',
      fullModel: '',
      year: '',
      enginecc: null,
      litres: null,
    });
  },

  /** Look a registration up. Resolves to { ok, vehicle } or { ok: false, error }. */
  async lookup(reg) {
    const clean = normalizeReg(reg);
    if (clean.length < 2) return { ok: false, error: 'Please enter your registration.' };
    try {
      const response = await fetch(`${LOOKUP_URL}?registration=${encodeURIComponent(clean)}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data || !data.make) {
        return { ok: false, error: 'We could not find that registration. Check it and try again, or choose your engine size below.' };
      }
      const vehicle = {
        vrm: normalizeReg(data.vrm || clean),
        make: data.make || '',
        model: data.model || '',
        fullModel: data.fullModel || data.model || '',
        year: data.year || '',
        enginecc: data.enginecc || null,
        litres: data.litres || null,
        fuel: data.fuel || '',
        showFullModelName: Boolean(data.showFullModelName),
        redirectUrl: data.redirectUrl || '',
        lookedUpAt: Date.now(),
      };
      this.saveVehicle(vehicle);
      return { ok: true, vehicle: this.getVehicle() };
    } catch (error) {
      return { ok: false, error: 'The lookup is not responding right now. Choose your engine size below, or try again in a moment.' };
    }
  },

  /** Line item properties for the current vehicle (or a typed reg). */
  properties(vehicle, typedReg) {
    const v = vehicle || this.getVehicle();
    const typed = normalizeReg(typedReg);
    const reg = typed || (v && v.vrm) || '';
    const props = {};
    if (reg) props[PROP_REG] = formatReg(reg);
    const manualLabel = v && v.manual
      ? `Not looked up - customer chose ${v.bandLabel || 'engine size'}${v.fuel ? `, ${titleCase(v.fuel)}` : ''}`
      : '';
    if (v && (!typed || typed === v.vrm)) {
      props[PROP_VEHICLE] = v.manual ? manualLabel : v.label;
    } else if (reg) {
      props[PROP_VEHICLE] = manualLabel || 'Registration typed by customer - please check';
    }
    return props;
  },

  /** Add one variant to the basket and open the cart drawer. */
  async addToCart({ id, quantity = 1, properties = {} }) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: [{ id: Number(id), quantity, properties }] }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json.status) {
      throw new Error(json.description || json.message || 'Sorry, we could not add that to your basket.');
    }
    let cart = null;
    try {
      cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    } catch (error) {
      cart = null;
    }
    document.dispatchEvent(
      new CartAddEvent(cart || {}, 'esk', {
        source: 'esk',
        itemCount: quantity,
        productId: json.product_id ? String(json.product_id) : undefined,
        variantId: String(id),
      })
    );
    openCartDrawer();
    return json;
  },

  on(handler) {
    document.addEventListener(EVENT_NAME, handler);
    handler(new CustomEvent(EVENT_NAME, { detail: { vehicle: this.getVehicle() } }));
    return () => document.removeEventListener(EVENT_NAME, handler);
  },
};

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) {
    toast('Added to your basket');
    return;
  }
  window.setTimeout(() => {
    const dialog = drawer.querySelector('dialog');
    if (dialog && dialog.open) return;
    if (typeof drawer.open === 'function') drawer.open();
  }, 120);
}

window.ESK = ESK;

/* ------------------------------------------------------- <esk-reg-form> */

class EskRegForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[data-reg]');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[data-status]');
    if (!this.form || !this.input) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.input.addEventListener(
      'input',
      () => {
        const pos = this.input.selectionStart;
        const before = this.input.value.length;
        this.input.value = formatReg(this.input.value);
        const after = this.input.value.length;
        if (pos !== null) {
          const next = Math.max(0, pos + (after - before));
          try {
            this.input.setSelectionRange(next, next);
          } catch (error) {
            /* ignore */
          }
        }
      },
      { signal }
    );

    this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });

    if (this.hasAttribute('data-prefill')) {
      const vehicle = ESK.getVehicle();
      if (vehicle && vehicle.vrm && !this.input.value) this.input.value = formatReg(vehicle.vrm);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  setStatus(text, state) {
    if (!this.status) return;
    this.status.textContent = text || '';
    if (state) this.status.dataset.state = state;
    else delete this.status.dataset.state;
  }

  async handleSubmit(event) {
    event.preventDefault();
    const reg = normalizeReg(this.input.value);
    if (reg.length < 2) {
      this.setStatus('Please enter your registration, e.g. AB12 CDE.', 'error');
      this.input.focus();
      return;
    }
    this.button?.classList.add('is-loading');
    this.button?.setAttribute('aria-busy', 'true');
    this.setStatus('Looking up your vehicle…');
    const result = await ESK.lookup(reg);
    this.button?.classList.remove('is-loading');
    this.button?.removeAttribute('aria-busy');

    if (result.ok) {
      this.setStatus(`Found: ${result.vehicle.label}`, 'ok');
      this.dispatchEvent(new CustomEvent('esk:lookup', { bubbles: true, detail: result }));
      const redirect = this.getAttribute('data-redirect');
      if (redirect) {
        window.location.href = redirect;
        return;
      }
      const target = this.getAttribute('data-scroll');
      if (target) {
        const el = document.querySelector(target);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    } else {
      this.setStatus(result.error, 'error');
      // A different reg that cannot be found must not leave the previous car's prices on screen.
      const saved = ESK.getVehicle();
      if (saved && saved.vrm !== reg) ESK.clear();
      this.dispatchEvent(new CustomEvent('esk:lookup', { bubbles: true, detail: { ok: false, reg } }));
      const manualTarget = this.getAttribute('data-manual');
      if (manualTarget) {
        const el = document.querySelector(manualTarget);
        if (el) {
          el.hidden = false;
          const regInput = el.querySelector('input[data-reg]');
          if (regInput && !regInput.value) regInput.value = formatReg(reg);
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }
}

/* ---------------------------------------------------------- <esk-finder> */

class EskFinder extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('script[type="application/json"]');
    try {
      this.data = dataEl ? JSON.parse(dataEl.textContent) : { kits: [] };
    } catch (error) {
      this.data = { kits: [] };
    }
    this.bar = this.querySelector('[data-vehicle-bar]');
    this.sizes = this.querySelector('[data-sizes]');
    this.cards = Array.from(this.querySelectorAll('[data-kit]'));
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener(
      'click',
      (event) => {
        const chip = event.target.closest('[data-band], [data-fuel]');
        if (chip && this.sizes && this.sizes.contains(chip)) {
          event.preventDefault();
          const group = chip.dataset.band !== undefined ? 'band' : 'fuel';
          this.sizes.querySelectorAll(`[data-${group}]`).forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
          return;
        }
        if (event.target.closest('[data-apply-manual]')) {
          event.preventDefault();
          this.applyManual();
          return;
        }
        if (event.target.closest('[data-change-vehicle]')) {
          event.preventDefault();
          this.changeVehicle();
          return;
        }
        if (event.target.closest('[data-show-manual]')) {
          event.preventDefault();
          if (this.sizes) {
            this.sizes.hidden = false;
            this.sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        const add = event.target.closest('button[data-add]');
        if (add) {
          event.preventDefault();
          this.addKit(add);
        }
      },
      { signal }
    );

    this.unsubscribe = ESK.on((event) => this.render(event.detail.vehicle));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.unsubscribe?.();
  }

  applyManual() {
    if (!this.sizes) return;
    const band = this.sizes.querySelector('[data-band][aria-pressed="true"]')?.dataset.band;
    const fuel = this.sizes.querySelector('[data-fuel][aria-pressed="true"]')?.dataset.fuel || '';
    const regInput = this.sizes.querySelector('input[data-reg]');
    const reg = normalizeReg(regInput?.value);
    const hint = this.sizes.querySelector('[data-sizes-hint]');
    if (!band) {
      if (hint) {
        hint.textContent = 'Choose your engine size first.';
        hint.dataset.state = 'error';
      }
      return;
    }
    if (reg.length < 2) {
      if (hint) {
        hint.textContent = 'Please add your registration so we can check the parts before dispatch.';
        hint.dataset.state = 'error';
      }
      regInput?.focus();
      return;
    }
    if (hint) {
      hint.textContent = '';
      delete hint.dataset.state;
    }
    ESK.setManual({ band, fuel, reg });
  }

  changeVehicle() {
    ESK.clear();
    const form = document.querySelector('esk-reg-form input[data-reg]');
    if (form) {
      form.value = '';
      form.focus();
      form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  render(vehicle) {
    const hasVehicle = Boolean(vehicle && vehicle.band);
    if (this.bar) {
      this.bar.classList.toggle('is-visible', hasVehicle);
      this.bar.classList.toggle('is-manual', Boolean(vehicle && vehicle.manual));
      if (hasVehicle) {
        const plate = this.bar.querySelector('[data-vehicle-plate]');
        const text = this.bar.querySelector('[data-vehicle-text]');
        const size = this.bar.querySelector('[data-vehicle-size]');
        if (plate) {
          plate.textContent = vehicle.vrm ? formatReg(vehicle.vrm) : '';
          plate.hidden = !vehicle.vrm;
        }
        if (text) text.textContent = vehicle.manual ? 'Engine size chosen by you' : vehicle.label;
        if (size) size.textContent = `Engine size: ${vehicle.bandLabel}${vehicle.fuel ? ` · ${titleCase(vehicle.fuel)}` : ''}`;
      }
    }
    if (this.sizes) {
      if (hasVehicle) this.sizes.hidden = true; // manual picker only appears when a lookup fails
      if (!hasVehicle) {
        const regInput = this.sizes.querySelector('input[data-reg]');
        if (regInput && !regInput.value && vehicle && vehicle.vrm) regInput.value = formatReg(vehicle.vrm);
      }
    }
    this.cards.forEach((card) => this.renderCard(card, vehicle));
  }

  renderCard(card, vehicle) {
    const handle = card.dataset.kit;
    const kit = (this.data.kits || []).find((k) => k.handle === handle);
    if (!kit) return;
    const priceEl = card.querySelector('[data-price]');
    const fromEl = card.querySelector('[data-price-from]');
    const sizeEl = card.querySelector('[data-price-size]');
    const noteEl = card.querySelector('[data-note]');
    const addBtn = card.querySelector('button[data-add]');
    const fuelEl = card.querySelector('[data-fuel-note]');

    const band = vehicle && vehicle.band;
    let variant = null;
    if (band) variant = kit.variants.find((v) => v.band === band) || null;

    if (fuelEl) {
      const kind = vehicle && vehicle.fuelKind;
      const label = kind === 'diesel' ? fuelEl.dataset.diesel : kind === 'petrol' ? fuelEl.dataset.petrol : '';
      fuelEl.textContent = label || '';
      fuelEl.classList.toggle('is-visible', Boolean(label));
    }

    if (variant) {
      if (fromEl) fromEl.hidden = true;
      if (priceEl) priceEl.textContent = money(variant.price);
      if (sizeEl) sizeEl.textContent = variant.title;
      if (addBtn) {
        addBtn.dataset.variant = String(variant.id);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? addBtn.dataset.labelAdd || 'Add to basket' : 'Sold out';
      }
      if (noteEl) {
        noteEl.textContent = '';
        delete noteEl.dataset.state;
      }
    } else {
      const cheapest = kit.variants.filter((v) => v.available).sort((a, b) => a.price - b.price)[0] || kit.variants[0];
      if (fromEl) fromEl.hidden = false;
      if (priceEl) priceEl.textContent = cheapest ? money(cheapest.price) : '';
      if (sizeEl) sizeEl.textContent = '';
      if (addBtn) {
        delete addBtn.dataset.variant;
        addBtn.disabled = true;
        addBtn.textContent = addBtn.dataset.labelAdd || 'Add to basket';
      }
      if (noteEl) {
        noteEl.textContent = band ? 'Not available for this engine size' : 'Enter your reg above to see your price';
        delete noteEl.dataset.state;
      }
    }
  }

  async addKit(button) {
    const card = button.closest('[data-kit]');
    const variantId = button.dataset.variant;
    const noteEl = card?.querySelector('[data-note]');
    if (!variantId) return;
    const vehicle = ESK.getVehicle();
    button.classList.add('is-loading');
    try {
      await ESK.addToCart({ id: variantId, quantity: 1, properties: ESK.properties(vehicle) });
      if (noteEl) {
        noteEl.textContent = 'Added to your basket';
        noteEl.dataset.state = 'ok';
      }
    } catch (error) {
      if (noteEl) {
        noteEl.textContent = error.message || 'Could not add to basket';
        noteEl.dataset.state = 'error';
      }
    } finally {
      button.classList.remove('is-loading');
    }
  }
}

/* ------------------------------------------------------ <esk-model-card> */

class EskModelCard extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('script[type="application/json"]');
    try {
      this.data = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (error) {
      this.data = null;
    }
    this.select = this.querySelector('select[data-variant-select]');
    this.priceEl = this.querySelector('[data-price]');
    this.regInput = this.querySelector('input[data-reg]');
    this.addBtn = this.querySelector('button[data-add]');
    this.noteEl = this.querySelector('[data-note]');
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.select?.addEventListener('change', () => this.updatePrice(), { signal });
    this.regInput?.addEventListener('input', () => {
      this.regInput.value = formatReg(this.regInput.value);
    }, { signal });
    this.addBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.add();
    }, { signal });

    this.unsubscribe = ESK.on((event) => this.applyVehicle(event.detail.vehicle));
    this.updatePrice();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.unsubscribe?.();
  }

  applyVehicle(vehicle) {
    if (!vehicle) return;
    if (this.regInput && vehicle.vrm && !this.regInput.value) this.regInput.value = formatReg(vehicle.vrm);
    if (this.select && vehicle.band) {
      const current = this.select.options[this.select.selectedIndex];
      const currentBand = bandFromTitle(current?.textContent);
      if (currentBand && currentBand !== vehicle.band) {
        const match = Array.from(this.select.options).find((o) => {
          const kitPart = o.textContent.split('/')[0].trim();
          return bandFromTitle(o.textContent) === vehicle.band && current.textContent.split('/')[0].trim() === kitPart;
        }) || Array.from(this.select.options).find((o) => bandFromTitle(o.textContent) === vehicle.band);
        if (match) {
          this.select.value = match.value;
          this.updatePrice();
        }
      }
    }
  }

  updatePrice() {
    if (!this.select || !this.priceEl) return;
    const option = this.select.options[this.select.selectedIndex];
    if (!option) return;
    this.priceEl.textContent = money(option.dataset.price);
    if (this.addBtn) {
      const available = option.dataset.available === 'true';
      this.addBtn.disabled = !available;
      this.addBtn.textContent = available ? this.addBtn.dataset.labelAdd || 'Add to basket' : 'Sold out';
    }
  }

  async add() {
    if (!this.select) return;
    const option = this.select.options[this.select.selectedIndex];
    const reg = normalizeReg(this.regInput?.value);
    if (this.regInput && reg.length < 2) {
      this.setNote('Please add your registration so we can check the parts for your exact engine.', 'error');
      this.regInput.focus();
      return;
    }
    this.addBtn?.classList.add('is-loading');
    try {
      const vehicle = ESK.getVehicle();
      const props = ESK.properties(vehicle, reg);
      if (!props[PROP_VEHICLE] && this.data?.title) props[PROP_VEHICLE] = `Model kit: ${this.data.title}`;
      await ESK.addToCart({ id: option.value, quantity: 1, properties: props });
      this.setNote('Added to your basket', 'ok');
    } catch (error) {
      this.setNote(error.message || 'Could not add to basket', 'error');
    } finally {
      this.addBtn?.classList.remove('is-loading');
    }
  }

  setNote(text, state) {
    if (!this.noteEl) return;
    this.noteEl.textContent = text;
    if (state) this.noteEl.dataset.state = state;
    else delete this.noteEl.dataset.state;
  }
}

/* ------------------------------------------------------ <esk-product-reg> */

class EskProductReg extends HTMLElement {
  connectedCallback() {
    this.regInput = this.querySelector('input[data-reg]');
    this.bar = this.querySelector('[data-vehicle-bar]');
    this.sizeEl = this.querySelector('[data-size-line]');
    this.errorEl = this.querySelector('[data-error]');
    this.required = this.dataset.required === 'true';
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-change-vehicle]')) {
        event.preventDefault();
        ESK.clear();
        if (this.regInput) {
          this.regInput.value = '';
          this.regInput.focus();
        }
      }
    }, { signal });

    this.regInput?.addEventListener('input', () => this.hideError(), { signal });

    this.unsubscribe = ESK.on((event) => this.render(event.detail.vehicle));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.unsubscribe?.();
  }

  get typedReg() {
    return normalizeReg(this.regInput?.value);
  }

  hideError() {
    this.errorEl?.classList.remove('is-visible');
  }

  showError(text) {
    if (!this.errorEl) return;
    this.errorEl.textContent = text;
    this.errorEl.classList.add('is-visible');
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.regInput?.focus();
  }

  /** Look the typed reg up, select the engine size, then click the add button again. */
  async checkThenAdd(reg, button) {
    if (this.busy) return;
    this.busy = true;
    this.hideError();
    const status = this.querySelector('[data-status]');
    if (status) {
      status.textContent = 'Checking your registration…';
      status.dataset.state = 'busy';
    }
    button.classList.add('is-loading');
    const result = await ESK.lookup(reg);
    if (!result.ok) {
      this.lookupFailedFor = reg;
      const saved = ESK.getVehicle();
      if (saved && saved.vrm !== reg) ESK.clear();
      if (status) {
        status.textContent = 'We could not find that registration - no problem, we will check it before dispatch.';
        status.dataset.state = 'warn';
      }
    } else {
      this.lookupFailedFor = '';
      if (status) {
        status.textContent = '';
        delete status.dataset.state;
      }
      await this.waitForVariantUpdate();
    }
    syncProductForms();
    button.classList.remove('is-loading');
    this.busy = false;
    if (button.isConnected && !button.disabled) button.click();
  }

  /** Resolve once Horizon has finished switching variant (or after a short timeout). */
  waitForVariantUpdate() {
    return new Promise((resolve) => {
      const picker = document.querySelector('variant-picker');
      const idInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      const wanted = this.pendingVariantId;
      if (!picker || !wanted || (idInput && String(idInput.value) === String(wanted))) {
        resolve();
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        document.removeEventListener('variant:update', finish);
        setTimeout(resolve, 50);
      };
      document.addEventListener('variant:update', finish);
      setTimeout(finish, 2000);
    });
  }

  render(vehicle) {
    const hasVehicle = Boolean(vehicle && vehicle.band);
    if (this.regInput && vehicle && vehicle.vrm && !this.regInput.value) this.regInput.value = formatReg(vehicle.vrm);
    if (this.bar) {
      this.bar.classList.toggle('is-visible', hasVehicle);
      this.bar.classList.toggle('is-manual', Boolean(vehicle && vehicle.manual));
      if (hasVehicle) {
        const plate = this.bar.querySelector('[data-vehicle-plate]');
        const text = this.bar.querySelector('[data-vehicle-text]');
        const size = this.bar.querySelector('[data-vehicle-size]');
        if (plate) {
          plate.textContent = vehicle.vrm ? formatReg(vehicle.vrm) : '';
          plate.hidden = !vehicle.vrm;
        }
        if (text) text.textContent = vehicle.manual ? 'Engine size chosen by you' : vehicle.label;
        if (size) size.textContent = `Engine size: ${vehicle.bandLabel}`;
      }
    }
    const ownForm = this.querySelector('esk-reg-form');
    if (ownForm) ownForm.hidden = hasVehicle && Boolean(vehicle.vrm);
    if (hasVehicle) this.selectBandVariant(vehicle.band);
    this.fitCheck(vehicle);
    syncProductForms();
  }

  /** Model-specific kits only: warn when the looked-up car is not the car the kit is for. */
  fitCheck(vehicle) {
    const warn = this.querySelector('[data-fit-warning]');
    if (!warn) return;
    const fitMake = String(this.dataset.fitMake || '').toLowerCase();
    if (!fitMake || !vehicle || vehicle.manual) {
      warn.hidden = true;
      return;
    }
    const fitModel = String(this.dataset.fitModel || '').toLowerCase();
    const fitFuel = String(this.dataset.fitFuel || '').toLowerCase();
    const make = String(vehicle.make || '').toLowerCase();
    const model = `${vehicle.model || ''} ${vehicle.fullModel || ''}`.toLowerCase();
    const aliases = {
      vw: ['vw', 'volkswagen'],
      volkswagen: ['vw', 'volkswagen'],
      vauxhall: ['vauxhall', 'opel'],
      mercedes: ['mercedes'],
      'mercedes-benz': ['mercedes'],
    };
    const makeOk = !make || (aliases[fitMake] || [fitMake]).some((alias) => make.includes(alias));
    const modelOk = !fitModel || !model.trim() || model.includes(fitModel);
    const fuelOk = !fitFuel || !vehicle.fuelKind || vehicle.fuelKind === 'other' || vehicle.fuelKind === fitFuel;
    const fits = makeOk && modelOk && fuelOk;
    warn.hidden = fits;
    if (!fits) {
      const title = warn.querySelector('[data-fit-title]');
      const text = warn.querySelector('[data-fit-text]');
      if (title) title.textContent = `This kit is for a ${this.dataset.fitLabel || 'different car'}.`;
      if (text) text.textContent = ` Your registration came back as a ${vehicle.label}, so this kit may not fit. `;
    }
  }

  /** Select the variant that matches the engine-size band through Horizon's variant picker. */
  selectBandVariant(band) {
    const picker = document.querySelector('variant-picker');
    if (!picker || !band) return;
    const want = BANDS[band].short.toLowerCase();
    const radios = Array.from(picker.querySelectorAll('fieldset input[type="radio"]'));
    const radio = radios.find((r) => String(r.value || '').toLowerCase().includes(want));
    if (radio) {
      this.pendingVariantId = radio.dataset.variantId || '';
      if (!radio.checked) radio.click();
      return;
    }
    const selects = Array.from(picker.querySelectorAll('select'));
    for (const select of selects) {
      const option = Array.from(select.options).find((o) => String(o.value || o.textContent).toLowerCase().includes(want));
      if (option && select.value !== option.value) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}

/* Attach registration properties to every add-to-cart form on the page.
   Horizon handles the submit in a capture-phase *document* listener and reads the form
   synchronously, so the hidden inputs must already be in the form. We keep them in sync
   whenever the vehicle or the typed reg changes, and guard the click/submit at *window*
   capture, which always runs before any document listener. */
function setHidden(form, name, value) {
  let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
  if (!value) {
    if (input) input.remove();
    return;
  }
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }
  input.value = value;
}

function isCartForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  const action = form.getAttribute('action') || '';
  return action.includes('/cart/add') || form.dataset.type === 'add-to-cart-form';
}

function cartForms() {
  return Array.from(document.querySelectorAll('form')).filter(isCartForm);
}

function regBlock() {
  return document.querySelector('esk-product-reg');
}

function syncProductForms() {
  const block = regBlock();
  if (!block) return;
  const vehicle = ESK.getVehicle();
  const reg = block.typedReg || (vehicle && vehicle.vrm) || '';
  const props = reg ? ESK.properties(vehicle, reg) : {};
  for (const form of cartForms()) {
    setHidden(form, `properties[${PROP_REG}]`, props[PROP_REG] || '');
    setHidden(form, `properties[${PROP_VEHICLE}]`, props[PROP_VEHICLE] || '');
  }
}

const REG_REQUIRED_MESSAGE = 'Please enter your registration so we can match the parts to your car.';

/** Returns true when the add may go ahead now. Blocks (and handles) it otherwise. */
function guardAddToCart(event, form, button, options = {}) {
  const block = regBlock();
  if (!block) return true;
  const vehicle = ESK.getVehicle();
  const typed = block.typedReg;
  const reg = typed || (vehicle && vehicle.vrm) || '';

  if (block.required && reg.length < 2) {
    event.preventDefault();
    event.stopImmediatePropagation();
    block.showError(REG_REQUIRED_MESSAGE);
    return false;
  }

  // A reg has been typed but not looked up yet: look it up first, pick the engine
  // size, then resume the add. If the lookup fails we still add (the reg is attached).
  const needsLookup = typed && typed !== (vehicle && vehicle.vrm) && block.lookupFailedFor !== typed;
  if (needsLookup && button && !options.express) {
    event.preventDefault();
    event.stopImmediatePropagation();
    block.checkThenAdd(typed, button);
    return false;
  }

  syncProductForms();
  return true;
}

window.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const pay = target?.closest('.shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout, shopify-buy-it-now-button');
    if (pay) {
      const payForm = pay.closest('form') || cartForms()[0];
      if (isCartForm(payForm)) guardAddToCart(event, payForm, null, { express: true });
      return;
    }
    const button = target?.closest('button, input[type="submit"]');
    if (!button) return;
    if ((button.getAttribute('type') || 'submit').toLowerCase() !== 'submit') return;
    const form = button.form || button.closest('form');
    if (!isCartForm(form)) return;
    guardAddToCart(event, form, button);
  },
  true
);

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!isCartForm(form)) return;
    guardAddToCart(event, form, null);
  },
  true
);

ESK.on(() => syncProductForms());
document.addEventListener('input', (event) => {
  if (event.target instanceof Element && event.target.closest('esk-product-reg')) syncProductForms();
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncProductForms, { once: true });
} else {
  syncProductForms();
}

/* --------------------------------------------------------- <esk-cart-reg> */

class EskCartReg extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', async (event) => {
      const button = event.target.closest('button[data-save-reg]');
      if (!button) return;
      event.preventDefault();
      const row = button.closest('[data-line]');
      const input = row?.querySelector('input[data-reg]');
      const reg = normalizeReg(input?.value);
      const note = row?.querySelector('[data-note]');
      if (reg.length < 2) {
        if (note) {
          note.textContent = 'Please enter the registration.';
          note.dataset.state = 'error';
        }
        return;
      }
      button.classList.add('is-loading');
      try {
        let properties = {};
        try {
          properties = JSON.parse(row.dataset.properties || '{}');
        } catch (error) {
          properties = {};
        }
        properties[PROP_REG] = formatReg(reg);
        if (!properties[PROP_VEHICLE]) properties[PROP_VEHICLE] = 'Registration typed by customer - please check';
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(row.dataset.line), properties }),
        });
        if (!response.ok) throw new Error('Could not save');
        window.location.reload();
      } catch (error) {
        if (note) {
          note.textContent = 'Sorry, that did not save. Please try again.';
          note.dataset.state = 'error';
        }
        button.classList.remove('is-loading');
      }
    }, { signal });

    this.querySelectorAll('input[data-reg]').forEach((input) => {
      input.addEventListener('input', () => {
        input.value = formatReg(input.value);
      }, { signal });
      const vehicle = ESK.getVehicle();
      if (vehicle && vehicle.vrm && !input.value) input.value = formatReg(vehicle.vrm);
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/* ------------------------------------------------------------ <esk-addon> */

class EskAddon extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button[data-add]');
    this.controller = new AbortController();
    this.button?.addEventListener('click', async (event) => {
      event.preventDefault();
      const id = this.button.dataset.variant;
      if (!id) return;
      this.button.classList.add('is-loading');
      try {
        await ESK.addToCart({ id, quantity: 1, properties: {} });
        this.button.textContent = 'Added';
      } catch (error) {
        toast(error.message || 'Could not add to basket');
      } finally {
        this.button.classList.remove('is-loading');
      }
    }, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

/* ------------------------------------------------------------- register */

const defineOnce = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
defineOnce('esk-reg-form', EskRegForm);
defineOnce('esk-finder', EskFinder);
defineOnce('esk-model-card', EskModelCard);
defineOnce('esk-product-reg', EskProductReg);
defineOnce('esk-cart-reg', EskCartReg);
defineOnce('esk-addon', EskAddon);

export { ESK };
